import Layout from "../components/Layout";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid2,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import ConfirmationModal from "../components/ConfirmationModal";
import NewQuestionForm from "../components/NewQuestionForm";
import ReorderModal from "../components/ReorderModal";
import {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  createUnitWithLevelsAndQuestions,
  getCourseById,
} from "../service/service";

function AddUnit() {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [unitName, setUnitName] = useState("");
  const [unitDescription, setUnitDescription] = useState("");
  const [isBonusUnit, setIsBonusUnit] = useState(false);
  const [levels, setLevels] = useState([]);
  const [levelQuestionForms, setLevelQuestionForms] = useState({});
  const [levelQuestionData, setLevelQuestionData] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [unitNameError, setUnitNameError] = useState("");
  const [unitDescriptionError, setUnitDescriptionError] = useState("");

  const levelCounterRef = useRef(0);
  const questionCountersRef = useRef({});
  const questionFormRefs = useRef({});

  const levelQuestionEntries = useMemo(() => {
    const entries = {};

    levels.forEach((level) => {
      const forms = levelQuestionForms[level.id] || [];
      const dataMap = levelQuestionData[level.id] || {};
      entries[level.id] = forms
        .map((form) => {
          const entry = dataMap[form.id];
          if (!entry?.payload) {
            return null;
          }
          return { ...entry, formId: form.id };
        })
        .filter(Boolean);
    });
    return entries;
  }, [levels, levelQuestionData, levelQuestionForms]);

  const totalQuestions = useMemo(
    () =>
      Object.values(levelQuestionEntries).reduce(
        (acc, questionList) => acc + (questionList?.length || 0),
        0,
      ),
    [levelQuestionEntries],
  );

  const createLevelId = useCallback(() => {
    levelCounterRef.current += 1;
    return `temp-level-${Date.now()}-${levelCounterRef.current}`;
  }, []);

  const getQuestionFormRef = (formId) => {
    if (!questionFormRefs.current[formId]) {
      questionFormRefs.current[formId] = createRef();
    }
    return questionFormRefs.current[formId];
  };

  const handleAddLevel = () => {
    const newLevelId = createLevelId();
    setLevels((prev) => [
      ...prev,
      { id: newLevelId, name: "", isCollapsed: false },
    ]);
    setLevelQuestionForms((prev) => ({ ...prev, [newLevelId]: [] }));
  };

  const handleToggleLevelCollapse = (levelId) => {
    setLevels((prev) =>
      prev.map((level) =>
        level.id === levelId
          ? { ...level, isCollapsed: !level.isCollapsed }
          : level,
      ),
    );
  };

  const handleRemoveLevel = (levelId) => {
    const formsForLevel = levelQuestionForms[levelId] || [];

    setLevels((prev) => prev.filter((level) => level.id !== levelId));
    setLevelQuestionForms((prev) => {
      const next = { ...prev };
      delete next[levelId];
      return next;
    });
    setLevelQuestionData((prev) => {
      const next = { ...prev };
      delete next[levelId];
      return next;
    });

    formsForLevel.forEach((form) => {
      if (questionFormRefs.current[form.id]) {
        delete questionFormRefs.current[form.id];
      }
    });
  };

  const handleLevelNameChange = (levelId, value) => {
    setLevels((prev) =>
      prev.map((level) =>
        level.id === levelId ? { ...level, name: value } : level,
      ),
    );
  };

  const handleAddQuestionForm = (levelId) => {
    const nextCount = (questionCountersRef.current[levelId] || 0) + 1;
    questionCountersRef.current[levelId] = nextCount;
    const formId = `new-form-${Date.now()}-${nextCount}`;

    setLevelQuestionForms((prev) => {
      const existing = prev[levelId] || [];
      return { ...prev, [levelId]: [...existing, { id: formId }] };
    });
    setSnackbar({ open: false, message: "", severity: "success" });
  };

  const handleRemoveQuestionForm = (levelId, formId) => {
    setLevelQuestionForms((prev) => {
      const existing = prev[levelId] || [];
      const nextLevelForms = existing.filter((form) => form.id !== formId);
      return { ...prev, [levelId]: nextLevelForms };
    });

    setLevelQuestionData((prev) => {
      const currentLevelData = prev[levelId];
      if (!currentLevelData) {
        return prev;
      }
      const nextLevelData = { ...currentLevelData };
      delete nextLevelData[formId];

      return { ...prev, [levelId]: nextLevelData };
    });

    if (questionFormRefs.current[formId]) {
      delete questionFormRefs.current[formId];
    }
  };

  const handleQuestionFormChange = (levelId, formId, data) => {
    setLevelQuestionData((prev) => {
      const currentLevelData = prev[levelId] || {};

      if (!data?.payload?.question?.trim()) {
        if (!currentLevelData[formId]) {
          return prev;
        }
        const nextLevelData = { ...currentLevelData };
        delete nextLevelData[formId];
        return { ...prev, [levelId]: nextLevelData };
      }

      return { ...prev, [levelId]: { ...currentLevelData, [formId]: data } };
    });
  };

  const handleReorderQuestionForms = (levelId, reorderedQuestions) => {
    const orderMap = new Map(
      reorderedQuestions.map((item) => [item._id, item.order]),
    );

    setLevelQuestionForms((prev) => {
      const currentForms = prev[levelId] || [];
      const sortedForms = [...currentForms].sort(
        (a, b) =>
          (orderMap.get(a.id) || Number.MAX_SAFE_INTEGER) -
          (orderMap.get(b.id) || Number.MAX_SAFE_INTEGER),
      );
      return { ...prev, [levelId]: sortedForms };
    });

    setSnackbar({
      open: true,
      message: "Questions reordered successfully.",
      severity: "success",
    });
  };

  const handleReorderLevels = (reorderedLevels) => {
    const orderMap = new Map(
      reorderedLevels.map((item) => [item._id, item.order]),
    );

    setLevels((prev) =>
      [...prev].sort(
        (a, b) =>
          (orderMap.get(a.id) || Number.MAX_SAFE_INTEGER) -
          (orderMap.get(b.id) || Number.MAX_SAFE_INTEGER),
      ),
    );

    setSnackbar({
      open: true,
      message: "Levels reordered successfully.",
      severity: "success",
    });
  };

  const getReorderableLevels = () =>
    levels.map((level, index) => ({
      _id: level.id,
      question: level.name.trim() || `Level ${index + 1}`,
    }));

  const getReorderableQuestions = (levelId) => {
    const forms = levelQuestionForms[levelId] || [];
    const dataMap = levelQuestionData[levelId] || {};

    return forms.map((form, index) => {
      const entry = dataMap[form.id];
      const questionText =
        entry?.payload?.question?.trim() || `Question ${index + 1}`;
      return { _id: form.id, question: questionText };
    });
  };

  const validateBeforeSave = () => {
    const errors = [];
    const trimmedUnitName = unitName.trim();
    const trimmedDescription = unitDescription.trim();

    if (!trimmedUnitName) {
      errors.push("Unit name is required.");
      setUnitNameError("Unit name is required.");
    } else {
      setUnitNameError("");
    }

    if (!trimmedDescription) {
      errors.push("Unit description is required.");
      setUnitDescriptionError("Unit description is required.");
    } else {
      setUnitDescriptionError("");
    }

    if (!levels.length) {
      errors.push("Add at least one level.");
    }

    return errors;
  };

  const buildSubmissionPayload = () => {
    // Validate the entries before building payload
    const baseErrors = validateBeforeSave();
    const compiledLevels = [];
    let compiledQuestionCount = 0;
    const formErrors = [];

    const formData = new FormData();
    const hasFile = (file) =>
      file instanceof File || file instanceof Blob || (file && typeof file.size === "number");

    levels.forEach((level, levelIndex) => {
      const trimmedLevelName = level.name.trim();
      if (!trimmedLevelName) {
        formErrors.push(`Provide a name for Level ${levelIndex + 1}.`);
      }

      const levelForms = levelQuestionForms[level.id] || [];
      if (!levelForms.length) {
        formErrors.push(
          `Add at least one question to Level ${levelIndex + 1}.`,
        );
        return;
      }

      let questionOrder = 1;
      const questionsForLevel = [];

      levelForms.forEach((form) => {
        const formRef = questionFormRefs.current[form.id];
        const result = formRef?.current?.validateAndBuild?.();

        if (!result) {
          formErrors.push(
            `Fix validation issues in Level ${levelIndex + 1}, Question ${questionOrder}.`,
          );
          return;
        }

        const { payload, attachments } = result;
        const attachmentMeta = {};

        // Assign deterministic field names and append files to FormData while we already have them
        if (hasFile(attachments?.questionAudioFile)) {
          const fieldName = `questionAudio_${levelIndex}_${questionOrder - 1}`;
          formData.append(fieldName, attachments.questionAudioFile);
          attachmentMeta.questionAudioField = fieldName;
        }

        if (hasFile(attachments?.hintAudioFile)) {
          const fieldName = `hintAudio_${levelIndex}_${questionOrder - 1}`;
          formData.append(fieldName, attachments.hintAudioFile);
          attachmentMeta.hintAudioField = fieldName;
        }

        if (Array.isArray(attachments?.optionAudioFiles) && attachments.optionAudioFiles.length) {
          const optionAudioFields = [];
          attachments.optionAudioFiles.forEach((optionAttachment, optionIndex) => {
            if (!hasFile(optionAttachment?.file) || !optionAttachment?.text) {
              return;
            }
            const fieldName = `optionAudio_${levelIndex}_${questionOrder - 1}_${optionIndex}`;
            formData.append(fieldName, optionAttachment.file);
            optionAudioFields.push({ text: optionAttachment.text, fieldName });
          });
          if (optionAudioFields.length) {
            attachmentMeta.optionAudioFields = optionAudioFields;
          }
        }

        questionsForLevel.push({
          payload: {
            ...payload,
            // Use the Unit ID from backend
            parentUnitId: null,
            parentCourseId: courseId,
            parentLevelId: null,
            order: questionOrder,
          },
          attachmentMeta,
        });
        questionOrder += 1;
      });

      compiledQuestionCount += questionsForLevel.length;

      compiledLevels.push({
        id: level.id,
        name: trimmedLevelName,
        order: levelIndex + 1,
        questions: questionsForLevel,
      });
    });

    const errors = [...baseErrors, ...formErrors].filter(Boolean);
    if (errors.length) {
      return { errors };
    }

    const payload = {
      unit: {
        // ID will be generated from the backend
        name: unitName.trim(),
        description: unitDescription.trim(),
        courseId,
        order: course.units.length + 1,
        isBonus: isBonusUnit,
      },
      levels: compiledLevels,
      stats: {
        levelCount: compiledLevels.length,
        questionCount: compiledQuestionCount,
      },
    };

    formData.append("fullPayload", JSON.stringify(payload));

    return { payload, formData };
  };

  const handleSaveUnit = async () => {
    setSnackbar({ open: false, message: "", severity: "success" });
    setIsSaving(true);

    const { payload, formData, errors } = buildSubmissionPayload();

    if (errors?.length) {
      setSnackbar({
        open: true,
        severity: "error",
        message: errors[0] || "Failed to prepare unit payload.",
      });
      setIsSaving(false);
      return;
    }

    formData.append("notificationType", "new-unit");

    console.log("Prepared payload for backend submission", payload);

    const response = await createUnitWithLevelsAndQuestions(formData || payload);

    console.log("Backend response", response);
    setSnackbar({
      open: true,
      severity: response.success ? "success" : "error",
      message: response.success
        ? `New Unit Created • ${payload.stats.levelCount} Levels • ${payload.stats.questionCount} Questions`
        : response.message,
    });
    setIsSaving(false);
    if (response.success) {
      // Reset form
      await fetchedCourse();
      setUnitName("");
      setUnitDescription("");
      setIsBonusUnit(false);
      setLevels([]);
      setLevelQuestionForms({});
      setLevelQuestionData({});
      questionFormRefs.current = {};
    }
  };

  const handleCloseSnackbar = (_event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const fetchedCourse = useCallback(async () => {
    const fetchedCourse = await getCourseById(courseId);
    setCourse(fetchedCourse.data);
  }, [courseId]);

  // Fetch course details
  useEffect(() => {
    fetchedCourse();
  }, [fetchedCourse]);

  return (
    <Layout>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          height: "100%",
          width: "100%",
          backgroundColor: "background.default",
          p: 4,
        }}
      >
        <Paper elevation={0} sx={{ padding: "2rem", width: "100%" }}>
          <Typography variant="h5" gutterBottom>
            Create New Unit
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Course name: {course?.name || courseId}
          </Typography>

          <Paper sx={{ mt: 3, mb: 3, p: 3, border: "1px solid silver" }}>
            <Grid2 container spacing={2}>
              <Grid2 size={12}>
                <Typography sx={{ mt: 1, mb: 2 }} variant="h6">
                  Unit Details
                </Typography>
                <Typography variant="subtitle1">
                  Unit Order:{" "}
                  {(course && course.units.length + 1) || "(not specified)"}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 10 }}>
                <TextField
                  label="Unit Name"
                  variant="outlined"
                  fullWidth
                  value={unitName}
                  onChange={(event) => {
                    setUnitName(event.target.value);
                    if (unitNameError) {
                      setUnitNameError("");
                    }
                  }}
                  error={Boolean(unitNameError)}
                  helperText={unitNameError || undefined}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isBonusUnit}
                      onChange={(event) => setIsBonusUnit(event.target.checked)}
                    />
                  }
                  label="Bonus"
                />
              </Grid2>
              <Grid2 size={12}>
                <TextField
                  label="Unit Description"
                  variant="outlined"
                  fullWidth
                  multiline
                  minRows={1}
                  value={unitDescription}
                  onChange={(event) => {
                    setUnitDescription(event.target.value);
                    if (unitDescriptionError) {
                      setUnitDescriptionError("");
                    }
                  }}
                  error={Boolean(unitDescriptionError)}
                  helperText={unitDescriptionError || ""}
                />
              </Grid2>
            </Grid2>
          </Paper>

          <Paper sx={{ mt: 2, mb: 3, p: 3, border: "1px solid silver" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                gap: 1,
              }}
            >
              <Typography variant="h6">Levels & Questions</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                {/* Reorder Levels Modal */}
                <ReorderModal
                  buttonTitle="Reorder Levels"
                  title="Reorder levels"
                  questions={getReorderableLevels()}
                  confirmLabel="Apply Order"
                  cancelLabel="Cancel"
                  onConfirm={handleReorderLevels}
                  isDisabled={levels.length < 2 || isSaving}
                />

                {/* Add Level Button */}
                <Button
                  variant="outlined"
                  color="info"
                  onClick={handleAddLevel}
                >
                  Add Level
                </Button>
              </Box>
            </Box>

            {!levels.length && (
              <Typography variant="body2" color="text.secondary">
                No levels added yet. Add the first level to start attaching
                questions.
              </Typography>
            )}

            {levels.map((level, levelIndex) => {
              const forms = levelQuestionForms[level.id] || [];
              const reorderableQuestions = getReorderableQuestions(level.id);

              return (
                <Paper
                  key={level.id}
                  sx={{ p: 2, mb: 3, border: "1px solid #e0e0e0" }}
                  elevation={0}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                      gap: 2,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Level {levelIndex + 1} •{" "}
                      {level.name.trim() || "(unnamed)"}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {/* Collapse/Expand Level Button */}
                      <Button
                        variant="outlined"
                        color="inherit"
                        endIcon={
                          level.isCollapsed ? <ExpandMore /> : <ExpandLess />
                        }
                        onClick={() => handleToggleLevelCollapse(level.id)}
                      >
                        {level.isCollapsed ? "Expand Level" : "Collapse Level"}
                      </Button>

                      {/* Remove Level Button */}
                      <ConfirmationModal
                        title="Remove level?"
                        description={`Are you sure you want to remove Level ${levelIndex + 1}? This will discard its questions in this form.`}
                        helperText="This action cannot be undone."
                        confirmLabel="Remove Level"
                        cancelLabel="Cancel"
                        renderTrigger={({ openModal, disabled }) => (
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={openModal}
                            disabled={disabled}
                          >
                            Remove Level
                          </Button>
                        )}
                        onConfirm={() => handleRemoveLevel(level.id)}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ display: level.isCollapsed ? "none" : "block" }}>
                    {/* Level Name Input Field */}
                    <Grid2 size={12} sx={{ mt: 3 }}>
                      <TextField
                        label="Level Name"
                        variant="outlined"
                        fullWidth
                        value={level.name}
                        onChange={(event) =>
                          handleLevelNameChange(level.id, event.target.value)
                        }
                      />
                    </Grid2>

                    {/* Questions Forms */}
                    {forms.map((form, questionIndex) => (
                      <NewQuestionForm
                        key={form.id}
                        ref={getQuestionFormRef(form.id)}
                        courseId={courseId}
                        // unitId={tempUnitId} // Use Unit ID from backend
                        levelId={null}
                        levelOrder={levelIndex + 1}
                        nextOrder={questionIndex + 1}
                        displayConfirmButton={false}
                        secondaryBtnText="Remove Question"
                        onChange={(data) =>
                          handleQuestionFormChange(level.id, form.id, data)
                        }
                        onCancel={() =>
                          handleRemoveQuestionForm(level.id, form.id)
                        }
                      />
                    ))}

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 2,
                        gap: 2,
                      }}
                    >
                      {/* Reorder Questions Modal Button */}
                      <ReorderModal
                        title={`Reorder questions for Level ${levelIndex + 1}`}
                        questions={reorderableQuestions}
                        confirmLabel="Apply Order"
                        cancelLabel="Cancel"
                        onConfirm={(items) =>
                          handleReorderQuestionForms(level.id, items)
                        }
                        isDisabled={reorderableQuestions.length < 2 || isSaving}
                      />

                      {/* Add Question Button */}
                      <Button
                        variant="outlined"
                        color="info"
                        onClick={() => handleAddQuestionForm(level.id)}
                      >
                        Add Question Form
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Paper>

          {/* Confirm New Unit Modal */}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <ConfirmationModal
              title="Confirm new unit"
              description={`Unit: ${unitName || "(unnamed)"}. Levels: ${levels.length}. Questions: ${totalQuestions}.`}
              helperText="This will create the new unit along with its levels and questions, and notify eligible learners."
              buttonLabel={isSaving ? "Preparing..." : "Review & Save Unit"}
              loadingLabel="Preparing..."
              confirmLabel="Confirm"
              cancelLabel="Cancel"
              disabled={isSaving}
              loading={isSaving}
              renderTrigger={({ openModal, disabled }) => (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={disabled}
                  onClick={openModal}
                >
                  {isSaving ? "Preparing..." : "Save Unit"}
                </Button>
              )}
              onConfirm={handleSaveUnit}
            />
          </Box>

          <Typography
            variant="caption"
            sx={{ display: "block", textAlign: "center", mt: 2 }}
          >
            Levels added: {levels.length} • Question payloads: {totalQuestions}
          </Typography>
        </Paper>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
}

export default AddUnit;
